import type { CSSProperties, ReactNode } from 'react';

const delay = (seconds: number): CSSProperties => ({ animationDelay: `${seconds}s` });

function Lock({ x, y }: { x: number; y: number }) {
  return <>
    <circle className="s-acc" cx={x} cy={y} r={8} />
    <path className="s-white-line" d={`M${x - 2.2} ${y - 0.6}v-2a2.2 2.2 0 0 1 4.4 0v2`} />
    <rect className="s-white" x={x - 3.6} y={y - 0.8} width={7.2} height={5.2} rx={1} />
  </>;
}

function Tick({ x, y, tone = 's-ok' }: { x: number; y: number; tone?: string }) {
  return <>
    <circle className={tone} cx={x} cy={y} r={6} />
    <path className="s-white-line" d={`M${x - 2.6} ${y}l1.8 1.9 3.4-3.6`} />
  </>;
}

function Script() {
  const lines: Array<[number, number, number, string?]> = [
    [84, 48, 84], [84, 56, 78], [84, 64, 56],
    [114, 78, 28, 's-mute'], [98, 86, 56], [98, 94, 44],
    [84, 108, 84], [84, 116, 70],
    [116, 130, 24, 's-mute'], [98, 138, 52], [98, 146, 36]
  ];
  const anchors: Array<[string, number]> = [['CHARACTER', 44], ['LOCATION', 78], ['PROP', 112]];
  return <>
    <rect className="s-panel" x={70} y={14} width={112} height={152} rx={4} />
    <text className="s-label" x={84} y={34}>INT. STAGE — NIGHT</text>
    {lines.map(([x, y, width, tone], i) => <rect key={i} className={tone || 's-ink'} x={x} y={y} width={width} height={3} rx={1.5} />)}
    <rect className="s-cover a-reveal" x={78} y={44} width={100} height={108} />
    <rect className="s-acc a-caret" x={80} y={42} width={2} height={8} />
    {anchors.map(([label, y], i) => <g key={label} className="a-anchor" style={delay(i * 0.35)}>
      <path className="s-dash" d={`M184 ${y + 10}H198`} />
      <rect className={i === 0 ? 's-panel s-hot' : 's-panel'} x={200} y={y} width={100} height={20} rx={4} />
      {i === 0 && <circle className="s-acc" cx={211} cy={y + 10} r={3.5} />}
      {i === 1 && <path className="s-mute" d={`M207 ${y + 13.5}l4-7 4 7z`} />}
      {i === 2 && <rect className="s-mute" x={207.5} y={y + 6.5} width={7} height={7} rx={1} />}
      <text className="s-label" x={222} y={y + 12.5}>{label}</text>
    </g>)}
  </>;
}

function Cast() {
  const cards: Array<[string, number]> = [['HERO', 18], ['MENTOR', 92], ['RIVAL', 166], ['HARBOR', 240]];
  return <>{cards.map(([label, x], i) => <g key={label}>
    <g className="a-rise" style={delay(i * 0.15)}>
      <rect className="s-panel" x={x} y={26} width={62} height={104} rx={4} />
      <rect className="s-screen" x={x + 5} y={31} width={52} height={70} rx={2} />
      {i < 3
        ? <><circle className="s-ink" cx={x + 31} cy={60} r={10} /><path className="s-ink" d={`M${x + 12} 101Q${x + 31} 68 ${x + 50} 101Z`} /></>
        : <><circle className="s-mute" cx={x + 18} cy={46} r={5} /><path className="s-ink" d={`M${x + 5} 101L${x + 22} 72L${x + 32} 84L${x + 42} 66L${x + 57} 101Z`} /></>}
      <rect className="s-ink" x={x + 5} y={109} width={40} height={3} rx={1.5} />
      <rect className="s-ink s-faint" x={x + 5} y={117} width={26} height={3} rx={1.5} />
      <text className="s-label" x={x + 31} y={150} textAnchor="middle">{label}</text>
    </g>
    <rect className="s-hot-frame a-frame" x={x} y={26} width={62} height={104} rx={4} style={delay(i * 0.25)} />
    <g className="a-snap" style={delay(i * 0.25)}><Lock x={x + 56} y={30} /></g>
  </g>)}</>;
}

function Clapper() {
  const stripes = (y: number) => [0, 1, 2, 3, 4, 5].map(i => <path key={i} className="s-deep" d={`M${50 + i * 21} ${y}h11l-8 12h-11z`} />);
  const slate: Array<[string, number, string]> = [['SCENE', 50, '03'], ['SHOT', 93, '02A'], ['TAKE', 136, '1']];
  return <>
    <rect className="s-panel" x={40} y={74} width={130} height={82} rx={3} />
    <rect className="s-ink" x={40} y={62} width={130} height={12} rx={2} />
    {stripes(62)}
    <g className="a-clap">
      <rect className="s-ink" x={40} y={48} width={130} height={12} rx={2} />
      {stripes(48)}
    </g>
    <circle className="s-mute" cx={45} cy={61} r={2.5} />
    <path className="s-acc-line a-flash" d="M178 50l8-6M180 60h10M178 70l8 5" />
    <path className="s-line" d="M40 118H170M83 80V118M126 80V118" />
    {slate.map(([label, x, value]) => <g key={label}>
      <text className="s-label" x={x} y={94}>{label}</text>
      <text className="s-value" x={x} y={111}>{value}</text>
    </g>)}
    <text className="s-label" x={50} y={134}>DIRECTOR</text>
    <text className="s-value" x={50} y={149}>CUE</text>
    <rect className="s-screen" x={194} y={48} width={108} height={76} rx={2} />
    <path className="s-line s-faint" d="M230 48V124M266 48V124M194 73H302M194 99H302" />
    <g className="a-pan">
      <circle className="s-ink" cx={248} cy={84} r={8} />
      <path className="s-ink" d="M230 124Q248 94 266 124Z" />
      <rect className="s-acc-line" x={238} y={74} width={20} height={20} rx={1} />
    </g>
    <path className="s-acc-line" d="M200 64V54H210M286 54H296V64M296 108V118H286M210 118H200V108" />
    <text className="s-label" x={194} y={142}>35MM · F2.8 · 24FPS</text>
  </>;
}

function Dailies() {
  const holes = Array.from({ length: 36 }, (_, i) => i);
  const frames = Array.from({ length: 9 }, (_, i) => i);
  return <>
    <g className="a-strip">
      <rect className="s-panel" x={-2} y={28} width={690} height={92} />
      {holes.map(i => <g key={i}>
        <rect className="s-deep" x={4 + i * 19} y={34} width={8} height={6} rx={1} />
        <rect className="s-deep" x={4 + i * 19} y={108} width={8} height={6} rx={1} />
      </g>)}
      {frames.map(i => {
        const x = 8 + i * 76;
        const variant = i % 4;
        return <g key={i}>
          <rect className={variant === 1 ? 's-screen s-hot' : 's-screen'} x={x} y={46} width={64} height={56} rx={2} />
          {variant === 0 && <><circle className="s-mute" cx={x + 48} cy={60} r={5} /><path className="s-ink" d={`M${x} 90L${x + 20} 72L${x + 34} 82L${x + 46} 68L${x + 64} 90V102H${x}Z`} /></>}
          {variant === 1 && <><circle className="s-ink" cx={x + 32} cy={68} r={7} /><path className="s-ink" d={`M${x + 17} 102Q${x + 32} 76 ${x + 47} 102Z`} /><Tick x={x + 56} y={54} tone="s-acc" /></>}
          {variant === 2 && <><circle className="s-ink" cx={x + 22} cy={74} r={5} /><path className="s-ink" d={`M${x + 12} 102Q${x + 22} 84 ${x + 32} 102Z`} /><circle className="s-ink" cx={x + 44} cy={72} r={5} /><path className="s-ink" d={`M${x + 34} 102Q${x + 44} 82 ${x + 54} 102Z`} /></>}
          {variant === 3 && <><rect className="s-ink" x={x + 8} y={70} width={12} height={32} /><rect className="s-ink" x={x + 24} y={60} width={14} height={42} /><rect className="s-ink" x={x + 42} y={76} width={12} height={26} /></>}
        </g>;
      })}
    </g>
    <text className="s-label" x={24} y={146}>DAILIES · SCENE 04</text>
    <rect className="s-ink" x={144} y={141} width={152} height={3} rx={1.5} />
    <rect className="s-acc a-progress" x={144} y={141} width={152} height={3} rx={1.5} />
    <text className="s-label" x={296} y={160} textAnchor="end">TAKE 03 · 00:42</text>
  </>;
}

function Timeline() {
  const ticks = Array.from({ length: 17 }, (_, i) => `M${44 + i * 16} 104v${i % 4 === 0 ? 6 : 3}`).join('');
  const video: Array<[number, number, boolean]> = [[42, 58, false], [102, 44, false], [148, 80, true], [230, 72, false]];
  return <>
    <rect className="s-screen" x={96} y={10} width={128} height={72} rx={2} />
    <circle className="s-mute" cx={196} cy={28} r={6} />
    <path className="s-ink" d="M96 70L126 48L146 62L170 40L224 72V82H96Z" />
    <text className="s-label" x={160} y={96} textAnchor="middle">00:01:12:08</text>
    <path className="s-line s-faint" d={ticks} />
    <text className="s-label" x={18} y={127}>V1</text>
    <text className="s-label" x={18} y={148}>A1</text>
    <rect className="s-screen" x={40} y={116} width={264} height={16} rx={2} />
    <rect className="s-screen" x={40} y={138} width={264} height={14} rx={2} />
    {video.map(([x, width, hot]) => <rect key={x} className={hot ? 's-soft s-hot' : 's-ink'} x={x} y={118} width={width} height={12} rx={2} />)}
    <rect className="s-ink s-faint" x={42} y={140} width={104} height={10} rx={2} />
    <rect className="s-ink s-faint" x={148} y={140} width={154} height={10} rx={2} />
    <g className="a-playhead">
      <path className="s-acc" d="M39 104h10l-5 6z" />
      <rect className="s-acc" x={43.25} y={108} width={1.5} height={48} />
    </g>
  </>;
}

function Review() {
  const checks = ['COLOR', 'SOUND', 'SPECS'];
  return <>
    <rect className="s-screen" x={60} y={12} width={200} height={108} rx={3} />
    <path className="s-ink" d="M60 100L100 72L124 86L160 60L220 94L260 80V104H60Z" />
    <circle className="s-panel" cx={160} cy={56} r={15} />
    <path className="s-text" d="M155 48v16l13-8z" />
    <rect className="s-ink" x={72} y={110} width={176} height={3} rx={1.5} />
    <rect className="s-acc a-fill" x={72} y={110} width={176} height={3} rx={1.5} />
    <path className="s-ink" d="M150 120l-4 10h28l-4-10z" />
    {checks.map((label, i) => <g key={label} className="a-check" style={delay(0.4 + i * 0.6)}>
      <rect className="s-panel" x={62 + i * 68} y={142} width={60} height={20} rx={4} />
      <Tick x={74 + i * 68} y={152} />
      <text className="s-label" x={84 + i * 68} y={154.5}>{label}</text>
    </g>)}
    <g transform="rotate(-10 214 40)">
      <g className="a-stamp">
        <rect className="s-ok-frame" x={170} y={28} width={88} height={24} rx={3} />
        <path className="s-ok-line" d="M178 40l3 3 6-6" />
        <text className="s-ok-label" x={193} y={44}>APPROVED</text>
      </g>
    </g>
  </>;
}

function Plate() {
  const trackers: Array<[number, number]> = [[64, 48], [256, 40], [80, 116], [244, 106]];
  return <>
    <rect className="s-screen" x={40} y={12} width={240} height={136} rx={2} />
    <path className="s-ink s-faint" d="M40 128C100 116 220 116 280 128V148H40Z" />
    <circle className="s-ink" cx={160} cy={60} r={11} />
    <path className="s-ink" d="M134 136Q160 74 186 136Z" />
    <path className="s-mask a-ants" d="M160 40C177 40 182 54 179 68C197 80 202 108 200 140H120C118 108 123 80 141 68C138 54 143 40 160 40Z" />
    {trackers.map(([x, y], i) => <g key={x} className="a-track" style={delay(i * 0.4)}>
      <path className="s-acc-line" d={`M${x - 7} ${y}h4m6 0h4M${x} ${y - 7}v4m0 6v4`} />
      <circle className="s-acc" cx={x} cy={y} r={1.5} />
    </g>)}
    <text className="s-label" x={48} y={26}>A012_C004 · PLATE</text>
    <text className="s-label" x={40} y={166}>MATTE TRACKED · 4 POINTS</text>
  </>;
}

function Compare() {
  const land = 'M40 120L92 76L124 100L176 58L240 108L280 88V142H40Z';
  return <>
    <defs><clipPath id="studio-scene-wipe"><rect className="a-wipe-clip" x={40} y={14} width={240} height={128} /></clipPath></defs>
    <rect className="s-screen" x={40} y={14} width={240} height={128} rx={2} />
    <circle className="s-mute" cx={228} cy={44} r={9} />
    <path className="s-ink" d={land} />
    <g clipPath="url(#studio-scene-wipe)">
      <rect className="s-soft" x={40} y={14} width={240} height={128} />
      <circle className="s-acc" cx={228} cy={44} r={9} />
      <path className="s-grade" d={land} />
    </g>
    <g className="a-wipe">
      <rect className="s-text" x={39.25} y={14} width={1.5} height={128} />
      <circle className="s-text" cx={40} cy={78} r={6} />
      <path className="s-deep-line" d="M37.5 75.5v5M42.5 75.5v5" />
    </g>
    <text className="s-label" x={48} y={30}>V02</text>
    <text className="s-label" x={272} y={134} textAnchor="end">V01</text>
    <text className="s-label" x={40} y={162}>A/B WIPE · GRADE V02 AGAINST PLATE</text>
  </>;
}

function Deliver() {
  const file = (x: number, y: number, label: string, className = 's-panel') => <>
    <path className={className} d={`M${x} ${y}h24l10 10v32h-34z`} />
    <path className="s-line" d={`M${x + 24} ${y}v10h10`} />
    <text className="s-label" x={x + 17} y={y + 31} textAnchor="middle">{label}</text>
  </>;
  return <>
    <g className="s-faint">{file(34, 70, '')}</g>
    {['EXR', 'MOV', 'MP4'].map((label, i) => <g key={label} className="a-ship" style={delay(i * 1.1)}>{file(34, 70, label)}</g>)}
    <path className="s-dash" d="M82 91H196" />
    <path className="s-line" d="M190 86l6 5-6 5" />
    <rect className="s-panel" x={214} y={68} width={80} height={60} rx={3} />
    <rect className="s-panel" x={210} y={58} width={88} height={12} rx={2} />
    <rect className="s-acc" x={250} y={58} width={8} height={70} />
    <g className="a-done"><Tick x={294} y={58} /></g>
    <text className="s-label" x={254} y={146} textAnchor="middle">DELIVERY</text>
    <text className="s-label" x={254} y={158} textAnchor="middle">manifest.json</text>
  </>;
}

function Chat() {
  return <>
    <g className="a-chat">
      <circle className="s-acc" cx={46} cy={40} r={11} />
      <text className="s-white-label" x={46} y={43.5} textAnchor="middle">C</text>
      <rect className="s-panel" x={64} y={24} width={160} height={32} rx={8} />
      <rect className="s-mute" x={76} y={34} width={120} height={3} rx={1.5} />
      <rect className="s-ink" x={76} y={43} width={84} height={3} rx={1.5} />
    </g>
    <g className="a-chat" style={delay(0.9)}>
      <rect className="s-soft s-hot" x={112} y={66} width={150} height={24} rx={8} />
      <rect className="s-acc s-faint" x={124} y={76.5} width={96} height={3} rx={1.5} />
      <circle className="s-ink" cx={280} cy={78} r={11} />
      <text className="s-white-label" x={280} y={81.5} textAnchor="middle">Y</text>
    </g>
    <g className="a-chat" style={delay(1.8)}>
      <text className="s-label" x={160} y={112} textAnchor="middle">+ COPYWRITER JOINED</text>
    </g>
    <g className="a-chat" style={delay(2.4)}>
      <circle className="s-panel" cx={46} cy={140} r={11} />
      <text className="s-label" x={46} y={142.5} textAnchor="middle">CW</text>
      <rect className="s-panel" x={64} y={128} width={56} height={24} rx={8} />
      {[80, 92, 104].map((x, i) => <circle key={x} className="s-mute a-dot" cx={x} cy={140} r={2.5} style={delay(i * 0.15)} />)}
    </g>
  </>;
}

function Brand() {
  const swatches = ['s-acc', 's-text', 's-ink', 's-panel'];
  return <>
    <text className="s-display" x={34} y={102}>Aa</text>
    <text className="s-label" x={38} y={126}>DISPLAY · 600</text>
    <path className="s-line s-faint" d="M38 138H150" />
    <text className="s-label" x={176} y={28}>PALETTE</text>
    {swatches.map((tone, i) => <rect key={tone} className={`${tone} a-rise`} x={176 + i * 31} y={36} width={26} height={44} rx={3} style={delay(i * 0.15)} />)}
    <text className="s-label" x={176} y={104}>TONE</text>
    <rect className="s-ink" x={176} y={116} width={122} height={3} rx={1.5} />
    <g className="a-knob"><circle className="s-knob" cx={184} cy={117.5} r={7} /></g>
    <text className="s-label" x={176} y={140}>CALM</text>
    <text className="s-label" x={298} y={140} textAnchor="end">BOLD</text>
  </>;
}

function Screens() {
  return <>
    <rect className="s-panel" x={44} y={12} width={232} height={142} rx={4} />
    <path className="s-line" d="M44 30H276" />
    {[54, 62, 70].map(cx => <circle key={cx} className="s-ink" cx={cx} cy={21} r={2} />)}
    <rect className="s-screen" x={90} y={18} width={120} height={6} rx={3} />
    <rect className="s-screen" x={52} y={38} width={44} height={108} rx={2} />
    {[0, 1, 2, 3, 4].map(i => <rect key={i} className={i === 0 ? 's-acc' : 's-ink'} x={58} y={46 + i * 12} width={28} height={3} rx={1.5} />)}
    <rect className="s-mute" x={106} y={40} width={90} height={5} rx={2.5} />
    <rect className="s-screen" x={106} y={54} width={76} height={44} rx={2} />
    <rect className="s-screen" x={190} y={54} width={76} height={44} rx={2} />
    <rect className="s-ink" x={198} y={64} width={40} height={4} rx={2} />
    <rect className="s-ink s-faint" x={198} y={74} width={56} height={3} rx={1.5} />
    <rect className="s-ink s-faint" x={198} y={82} width={48} height={3} rx={1.5} />
    <rect className="s-screen" x={106} y={106} width={160} height={40} rx={2} />
    <path className="s-acc-line" d="M112 136L140 124L164 130L196 114L226 120L260 110" />
    <rect className="s-shutter a-shutter" x={190} y={54} width={76} height={44} rx={2} />
    <path className="s-acc-line a-bracket" d="M184 62V48H198M258 48H272V62M272 90V104H258M198 104H184V90" />
    <text className="s-label" x={44} y={170}>CAPTURE · 1440×900 · 12 SCREENS</text>
  </>;
}

function Layers() {
  const layers: Array<[number, string]> = [[118, 'BACKGROUND'], [104, 'INTERFACE'], [90, 'CONTROLS']];
  return <>{layers.map(([y, label], i) => <g key={label} className={i ? `a-lift-${i}` : undefined}>
    <g transform={`translate(150 ${y}) scale(1 .55) rotate(45)`}>
      <rect className={['s-screen', 's-panel', 's-glass'][i]} x={-50} y={-50} width={100} height={100} rx={4} vectorEffect="non-scaling-stroke" />
      {i === 1 && <><rect className="s-ink s-faint" x={-40} y={-40} width={34} height={80} rx={2} /><rect className="s-ink s-faint" x={4} y={-40} width={36} height={36} rx={2} /></>}
      {i === 2 && <rect className="s-acc" x={6} y={8} width={32} height={32} rx={3} />}
    </g>
    <path className="s-line" d={`M226 ${y}H236`} />
    <text className="s-label" x={240} y={y + 3}>{label}</text>
  </g>)}</>;
}

function Storyboard() {
  const beats = ['HOOK', 'PROBLEM', 'DEMO', 'PROOF', 'OUTCOME', 'CTA'];
  return <>{beats.map((beat, i) => {
    const x = 28 + (i % 3) * 92;
    const y = 20 + Math.floor(i / 3) * 78;
    const subject = x + 18 + ((i * 17) % 44);
    return <g key={beat} className="a-rise" style={delay(i * 0.2)}>
      <rect className={i === 2 ? 's-screen s-hot' : 's-screen'} x={x} y={y} width={80} height={50} rx={2} />
      <path className="s-line" d={`M${x + 6} ${y + 40}L${x + 26} ${y + 28}L${x + 38} ${y + 35}L${x + 54} ${y + 22}L${x + 74} ${y + 40}`} />
      <circle className="s-line" cx={subject} cy={y + 18} r={5} />
      <text className="s-label" x={x} y={y + 62}>{`0${i + 1}`}</text>
      <text className="s-label" x={x + 80} y={y + 62} textAnchor="end">{beat}</text>
    </g>;
  })}</>;
}

function Motion() {
  return <>
    <rect className="s-panel" x={36} y={14} width={248} height={144} rx={4} />
    <rect className="s-mute" x={52} y={30} width={100} height={6} rx={3} />
    <rect className="s-ink" x={52} y={44} width={160} height={3} rx={1.5} />
    <rect className="s-ink s-faint" x={52} y={52} width={140} height={3} rx={1.5} />
    {[52, 128, 204].map(x => <rect key={x} className="s-screen" x={x} y={70} width={x === 204 ? 64 : 68} height={40} rx={2} />)}
    <path className="s-dash" d="M78 46Q170 40 224 132" />
    <g className="a-press"><rect className="s-acc" x={196} y={122} width={72} height={24} rx={4} /><text className="s-white-label" x={232} y={137.5} textAnchor="middle">Get started</text></g>
    <circle className="s-acc-line a-ring" cx={224} cy={134} r={10} />
    <g className="a-cursor"><path className="s-cursor" d="M0 0V14L4 10.5L7 16.5L9.5 15.5L6.5 9.5H11Z" /></g>
  </>;
}

function Waveform() {
  const bars = Array.from({ length: 30 }, (_, i) => i);
  return <>
    <rect className="s-panel" x={34} y={58} width={20} height={34} rx={10} />
    <path className="s-line" d="M28 80a16 16 0 0 0 32 0M44 96v12M36 108h16" />
    {bars.map(i => {
      const height = 8 + ((i * 37) % 9) * 6;
      return <rect key={i} className={`${i < 14 ? 's-acc' : 's-ink'} a-eq`} x={84 + i * 7} y={84 - height / 2} width={4} height={height} rx={2} style={delay(((i * 13) % 7) * 0.1)} />;
    })}
    <path className="s-line s-faint" d="M84 128H292" />
    <rect className="s-acc" x={181} y={122} width={1.5} height={12} />
    <text className="s-label" x={84} y={148}>VO · TAKE 2 · −14 LUFS</text>
  </>;
}

function Upscale() {
  return <>
    <defs>
      <clipPath id="studio-scene-px"><rect className="a-px-clip" x={40} y={16} width={240} height={124} /></clipPath>
      <pattern id="studio-scene-grid" x={40} y={16} width={10} height={10} patternUnits="userSpaceOnUse"><path className="s-grid" d="M10 0H0V10" /></pattern>
    </defs>
    <rect className="s-screen" x={40} y={16} width={240} height={124} rx={2} />
    <circle className="s-mute" cx={208} cy={48} r={10} />
    <path className="s-ink" d="M40 118L96 70L130 98L178 56L236 108L280 86V140H40Z" />
    <g clipPath="url(#studio-scene-px)">
      <rect className="s-blur" x={40} y={16} width={240} height={124} />
      <rect fill="url(#studio-scene-grid)" x={40} y={16} width={240} height={124} />
    </g>
    <g className="a-scan"><rect className="s-acc" x={39.25} y={16} width={1.5} height={124} /></g>
    <rect className="s-panel" x={48} y={24} width={40} height={14} rx={2} />
    <text className="s-label" x={68} y={33.5} textAnchor="middle">1080P</text>
    <rect className="s-acc" x={240} y={24} width={32} height={14} rx={2} />
    <text className="s-white-label" x={256} y={34} textAnchor="middle">4K</text>
    <text className="s-label" x={40} y={160}>1920×1080 → 3840×2160 · 24 FPS</text>
  </>;
}

const SCENES: Record<string, () => ReactNode> = {
  script: Script, cast: Cast, clapper: Clapper, dailies: Dailies, timeline: Timeline, review: Review,
  plate: Plate, compare: Compare, deliver: Deliver, chat: Chat, brand: Brand, screens: Screens,
  layers: Layers, storyboard: Storyboard, motion: Motion, waveform: Waveform, upscale: Upscale
};

const STAGE_SCENES: Record<string, string> = {
  'film:idea': 'script', 'film:world': 'cast', 'film:plan': 'clapper', 'film:generate': 'dailies', 'film:edit': 'timeline', 'film:review': 'review',
  'vfx:edit': 'plate', 'vfx:review': 'compare', 'vfx:deliver': 'deliver',
  'marketing:onboard': 'chat',
  'saas:brief': 'script', 'saas:brand': 'brand', 'saas:screens': 'screens', 'saas:rebuild': 'layers', 'saas:storyboard': 'storyboard', 'saas:motion': 'motion', 'saas:audio': 'waveform', 'saas:review': 'review',
  'upscale:upscale': 'upscale'
};

export default function PipelineScene({ kind, stage }: { kind: string; stage: string }) {
  const Scene = SCENES[STAGE_SCENES[`${kind}:${stage}`]] || Clapper;
  return <div className="studio-scene" aria-hidden="true">
    <svg viewBox="0 0 320 180" preserveAspectRatio="xMidYMid meet"><Scene /></svg>
  </div>;
}
